import { brandColors, semanticColors, connectorBannerColors } from '../theme/tokens/colors';
import { getConnectorStatusDescription } from './ChargePointHelper';

export const connectorStatusTone = {
    available: 'available',
    busy: 'busy',
    unavailable: 'unavailable',
};

const connectorStatuses = {
    available: 'Available',
    preparing: 'Preparing',
    charging: 'Charging',
    suspendedEV: 'SuspendedEV',
    suspendedEVSE: 'SuspendedEVSE',
    finishing: 'Finishing',
};

const normalize = (value) => (value || '').toString().trim();

export const getConnectorStatusTone = (chargePointStatus, connectorStatus) => {
    const normalizedChargePointStatus = normalize(chargePointStatus);
    const normalizedConnectorStatus = normalize(connectorStatus);

    if (normalizedChargePointStatus !== 'Connected') {
        return connectorStatusTone.unavailable;
    }

    if (normalizedConnectorStatus === connectorStatuses.available || normalizedConnectorStatus === connectorStatuses.preparing) {
        return connectorStatusTone.available;
    }

    if (
        normalizedConnectorStatus === connectorStatuses.charging ||
        normalizedConnectorStatus === connectorStatuses.suspendedEV ||
        normalizedConnectorStatus === connectorStatuses.suspendedEVSE ||
        normalizedConnectorStatus === connectorStatuses.finishing
    ) {
        return connectorStatusTone.busy;
    }

    return connectorStatusTone.unavailable;
};

export const getConnectorStatusColor = (chargePointStatus, connectorStatus) => {
    const tone = getConnectorStatusTone(chargePointStatus, connectorStatus);

    switch (tone) {
        case connectorStatusTone.available:
            return brandColors.green;
        case connectorStatusTone.busy:
            return brandColors.yellow;
        default:
            return brandColors.red;
    }
};

/*
 * spec 000220 (Part C) — `isWaitingForCharging` is the connector screen's 000023 window. Inside it the banner presents
 * an early SuspendedEV/EVSE as "waiting for charging"; the title must say the same thing, not "מושהה ע"י הרכב".
 */
export const getConnectorStatusTitlePresentation = (translatedData, chargePointStatus, connectorStatus, { isWaitingForCharging = false } = {}) => {
    const normalizedConnectorStatus = normalize(connectorStatus);
    const getLocalizedTitle = (translationPath, fallback) => {
        const translationValue = translatedData && translatedData.general
            ? translatedData.general.connectorStatusTitleOverrides?.[translationPath]
            : '';
        return translationValue || fallback;
    };

    if (normalizedConnectorStatus === connectorStatuses.charging) {
        return {
            title: getLocalizedTitle('charging', getConnectorStatusDescription(translatedData, connectorStatuses.charging)),
            color: brandColors.green,
        };
    }

    if (normalizedConnectorStatus === connectorStatuses.suspendedEV || normalizedConnectorStatus === connectorStatuses.suspendedEVSE) {
        if (isWaitingForCharging) {
            return {
                title: getLocalizedTitle('waitingForCharging', getConnectorStatusDescription(translatedData, connectorStatus)),
                color: connectorBannerColors.waiting,
            };
        }
        return {
            title: getLocalizedTitle('suspended', getConnectorStatusDescription(translatedData, connectorStatus)),
            // Surface the OCPP 1.6 suspend reason (by vehicle vs by charger) inline, next to the title.
            // Compact reason-only label so it reads e.g. "מושהה ע"י הרכב" across the row without repeating "מושהה".
            subtitle: getConnectorStatusDescription(translatedData, `${normalizedConnectorStatus}Reason`),
            color: brandColors.yellow,
        };
    }

    if (normalizedConnectorStatus === connectorStatuses.finishing) {
        return {
            title: getLocalizedTitle('finishing', getConnectorStatusDescription(translatedData, connectorStatuses.finishing)),
            color: semanticColors.textPrimary,
        };
    }

    return {
        title: getConnectorStatusDescription(translatedData, connectorStatus),
        color: getConnectorStatusColor(chargePointStatus, connectorStatus),
    };
};
